import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    Sparkles, Target, Compass, Users, Award, Layers,
    Cloud, ShieldCheck, LineChart, Code2, ServerCog, Wrench,
    Cpu, ArrowUpRight, CheckCircle2, GraduationCap, Building2,
    Rocket, Globe2,
} from "lucide-react";
import BgLayer from "@/components/ui-shared/BgLayer";
import { IMAGES } from "@/lib/data";
import { LOGOS } from "@/lib/vendors";

const container = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.07 } },
};
const item = {
    hidden: { opacity: 0, y: 18 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

/* ---------- Data ---------- */
const WHY_ITEMS = [
    { icon: Sparkles, title: "AI & Generative AI Specialists" },
    { icon: Building2, title: "Enterprise-Focused Learning" },
    { icon: GraduationCap, title: "Instructor-Led Training" },
    { icon: Wrench, title: "Practical Learning Experience" },
    { icon: Award, title: "Global Certification Preparation" },
    { icon: Layers, title: "Flexible Delivery Models (Virtual, Classroom, Onsite, Bootcamps & Enterprise Academies)" },
];

const PORTFOLIO = [
    { icon: Cpu, title: "Artificial Intelligence & Generative AI", desc: "Foundation models, RAG, agents, applied AI engineering." },
    { icon: Cloud, title: "Cloud Computing (AWS, Azure, Google Cloud)", desc: "Multi-cloud architecture, migration, and modernisation." },
    { icon: ServerCog, title: "SAP Technologies", desc: "S/4HANA, Fiori, BTP, ABAP and functional streams." },
    { icon: ShieldCheck, title: "Cybersecurity", desc: "Zero trust, SOC, cloud security, offensive & defensive." },
    { icon: LineChart, title: "Data & Analytics", desc: "Data engineering, warehousing, analytics, and MLOps." },
    { icon: Wrench, title: "DevOps & Automation", desc: "CI/CD, IaC, containers, SRE, and platform engineering." },
    { icon: Code2, title: "Software Development", desc: "Modern full-stack, microservices, and cloud-native." },
    { icon: Compass, title: "IT Service Management", desc: "ITIL 4, ITSM tooling, and enterprise service design." },
];

const PARTNERS = [
    { name: "SAP", slug: "sap" },
    { name: "Microsoft", slug: "microsoft" },
    { name: "AWS", slug: "aws" },
    { name: "Google Cloud", slug: "gcp" },
    { name: "IBM", slug: "ibm" },
    { name: "Oracle", slug: "oracle" },
    { name: "Red Hat", slug: "redhat" },
    { name: "Dell EMC", slug: "dell-emc" },
    { name: "MuleSoft", slug: "mulesoft" },
    { name: "Snowflake", slug: "snowflake" },
    { name: "Broadcom", slug: "broadcom" },
    { name: "GitHub", slug: "github" },
    { name: "Check Point", slug: "check-point" },
    { name: "CSA", slug: "csa" },
    { name: "PeopleCert", slug: null },
    { name: "AXELOS", slug: null },
    { name: "ISTQB", slug: "istqb" },
    { name: "CompTIA", slug: "comptia" },
    { name: "Elastic", slug: "elastic" },
    { name: "Siemens", slug: "siemens" },
    { name: "Lightbend", slug: "lightbend" },
    { name: "Android ATC", slug: "android-atc" },
    { name: "Mirantis", slug: "mirantis" },
    { name: "ISC2", slug: "isc2" },
    { name: "BCSM", slug: null },
];

const INDUSTRIES = [
    "IT", "BFSI", "Manufacturing", "Healthcare", "Retail", "Telecom",
    "Automotive", "Energy", "Government", "Education", "Logistics", "Consulting",
];

/* ---------- Sections ---------- */
const Hero = () => (
    <section className="relative pt-32 md:pt-40 pb-20 overflow-hidden">
        <BgLayer variant="mesh" intensity="strong" />
        <div className="sxe-container relative grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center">
            <motion.div initial="hidden" animate="show" variants={container}>
                <motion.div variants={item} className="overline"><span className="dot" /> About SpringXE</motion.div>
                <motion.h1
                    variants={item}
                    className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tighter leading-[1.02] text-white mt-6"
                    data-testid="about-hero-heading"
                >
                    <span className="text-gradient">SpringXE</span>
                </motion.h1>
                <motion.div variants={item} className="mt-3 text-[13px] uppercase tracking-[0.28em] text-[#C266FF] font-display">
                    Spring EdTech Pvt. Ltd.
                </motion.div>
                <motion.p variants={item} className="mt-4 text-white/90 font-display text-xl md:text-2xl tracking-tight">
                    Xelerating Careers. Building Futures.
                </motion.p>
                <motion.p variants={item} className="text-[#B0A3C4] text-lg leading-relaxed mt-6 max-w-2xl">
                    SpringXE – Spring EdTech Pvt. Ltd. is a leading enterprise learning company specializing in Artificial Intelligence (AI), Generative AI, Cloud, Cybersecurity, Data, SAP, DevOps, and Digital Transformation training. We help organizations build future-ready teams through instructor-led learning, certification programs, and customized capability development aligned with business goals.
                </motion.p>
                <motion.div variants={item} className="mt-8 flex flex-wrap gap-3">
                    <Link to="/partners" className="btn-primary" data-testid="about-cta-partners">
                        Explore Partner Programs <ArrowUpRight className="w-4 h-4" />
                    </Link>
                    <a href="#contact" className="btn-secondary" data-testid="about-cta-contact">Talk to Our Experts</a>
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
                className="relative rounded-2xl overflow-hidden border border-[#C266FF]/25"
            >
                <img src={IMAGES.enterpriseTeam} alt="SpringXE enterprise team" className="w-full h-[420px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#07020F] via-[#07020F]/30 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 grid grid-cols-3 gap-3">
                    {[
                        { k: "27+", l: "OEM partners" },
                        { k: "300+", l: "certified courses" },
                        { k: "12", l: "industries served" },
                    ].map((s) => (
                        <div key={s.l} className="rounded-xl bg-white/[0.06] backdrop-blur-xl border border-white/10 px-3 py-2.5">
                            <div className="font-display text-white text-2xl leading-none">{s.k}</div>
                            <div className="text-[10px] uppercase tracking-widest text-[#E9CCFF] font-display mt-1">{s.l}</div>
                        </div>
                    ))}
                </div>
            </motion.div>
        </div>
    </section>
);

const WhoWeAre = () => (
    <section className="sxe-section relative bg-[#08031a]" data-testid="about-who-section">
        <BgLayer variant="dots" />
        <div className="sxe-container relative grid lg:grid-cols-[1fr_1.1fr] gap-14 items-center">
            <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative rounded-2xl overflow-hidden border border-[#C266FF]/25"
            >
                <img src={IMAGES.aiAbstract} alt="Bridging skill gaps in emerging technology" className="w-full h-[380px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#07020F]/70 via-transparent to-[#A100FF]/10" />
                <div className="absolute top-5 left-5 flex items-center gap-2 bg-white/[0.06] backdrop-blur-xl border border-white/10 rounded-full px-3.5 py-1.5">
                    <Users className="w-3.5 h-3.5 text-[#E9CCFF]" />
                    <span className="text-[11px] uppercase tracking-widest text-[#E9CCFF] font-display">Who we are</span>
                </div>
            </motion.div>

            <div>
                <div className="overline"><span className="dot" /> Who We Are</div>
                <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-[1.05] text-white mt-4">
                    Enterprise learning that <span className="text-gradient">bridges skill gaps</span>.
                </h2>
                <p className="text-[#B0A3C4] text-lg leading-relaxed mt-6">
                    SpringXE is the enterprise learning division of Spring EdTech Pvt. Ltd., dedicated to helping organizations bridge critical skill gaps in emerging technologies. We partner with global technology leaders to deliver hands-on, instructor-led corporate training programs that prepare professionals for today&rsquo;s rapidly evolving technology landscape.
                </p>
            </div>
        </div>
    </section>
);

const MissionVision = () => (
    <section className="sxe-section relative" data-testid="about-mission-vision">
        <BgLayer variant="orbs" />
        <div className="sxe-container relative">
            <div className="grid md:grid-cols-2 gap-6">
                {[
                    {
                        overline: "Our Mission",
                        icon: Target,
                        title: "Empower organizations with future-ready talent.",
                        body: "To empower organizations with future-ready talent by delivering world-class AI, Generative AI, cloud, and technology training that drives innovation, productivity, and business growth.",
                        hue: "#A100FF",
                    },
                    {
                        overline: "Our Vision",
                        icon: Compass,
                        title: "The most trusted enterprise learning partner.",
                        body: "To become the most trusted enterprise learning partner for organizations accelerating digital transformation through AI and emerging technologies.",
                        hue: "#7B00CC",
                    },
                ].map((card) => {
                    const Icon = card.icon;
                    return (
                        <motion.div
                            key={card.overline}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.5 }}
                            className="relative rounded-2xl p-8 border border-white/[0.08] bg-gradient-to-br from-white/[0.04] to-white/[0.01] overflow-hidden"
                        >
                            <div
                                className="absolute -top-16 -right-10 w-64 h-64 rounded-full blur-3xl opacity-30"
                                style={{ background: `radial-gradient(circle, ${card.hue}, transparent 70%)` }}
                            />
                            <div className="relative">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#A100FF]/25 to-[#7B00CC]/10 border border-[#C266FF]/25 flex items-center justify-center">
                                    <Icon className="w-5 h-5 text-[#E9CCFF]" />
                                </div>
                                <div className="overline mt-5"><span className="dot" /> {card.overline}</div>
                                <h3 className="font-display text-white text-2xl md:text-3xl font-medium tracking-tighter leading-[1.15] mt-3">
                                    {card.title}
                                </h3>
                                <p className="text-[#B0A3C4] text-[15.5px] leading-relaxed mt-5">
                                    {card.body}
                                </p>
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </div>
    </section>
);

const WhyChoose = () => (
    <section className="sxe-section relative bg-[#08031a]" data-testid="about-why-section">
        <BgLayer variant="dots" />
        <div className="sxe-container relative">
            <div className="max-w-3xl">
                <div className="overline"><span className="dot" /> Why Enterprises Choose SpringXE</div>
                <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-[1.05] text-white mt-4">
                    Built for the way <span className="text-gradient">enterprises actually learn</span>.
                </h2>
            </div>
            <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                variants={container}
                className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12"
            >
                {WHY_ITEMS.map((w) => {
                    const Icon = w.icon;
                    return (
                        <motion.div
                            key={w.title}
                            variants={item}
                            className="group sxe-card p-6 hover:border-[#C266FF]/40 transition-colors"
                            data-testid={`about-why-item-${w.title.split(" ")[0].toLowerCase()}`}
                        >
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#A100FF]/25 to-[#7B00CC]/10 border border-[#C266FF]/25 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                                    <Icon className="w-5 h-5 text-[#E9CCFF]" />
                                </div>
                                <CheckCircle2 className="w-4 h-4 text-[#C266FF] ml-auto opacity-60" />
                            </div>
                            <div className="font-display text-white text-[17px] font-semibold tracking-tight leading-snug mt-5">
                                {w.title}
                            </div>
                        </motion.div>
                    );
                })}
            </motion.div>
        </div>
    </section>
);

const Portfolio = () => (
    <section className="sxe-section relative" data-testid="about-portfolio-section">
        <BgLayer variant="mesh" />
        <div className="sxe-container relative">
            <div className="max-w-3xl">
                <div className="overline"><span className="dot" /> Our Training Portfolio</div>
                <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-[1.05] text-white mt-4">
                    Eight capability streams. <span className="text-gradient">One certified stack.</span>
                </h2>
            </div>
            <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                variants={container}
                className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12"
            >
                {PORTFOLIO.map((p) => {
                    const Icon = p.icon;
                    return (
                        <motion.div
                            key={p.title}
                            variants={item}
                            className="group sxe-card p-6 flex flex-col hover:border-[#C266FF]/40 hover:-translate-y-1 transition-all duration-400"
                            data-testid={`about-portfolio-${p.title.split(" ")[0].toLowerCase()}`}
                        >
                            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#A100FF]/25 to-[#7B00CC]/10 border border-[#C266FF]/25 flex items-center justify-center">
                                <Icon className="w-5 h-5 text-[#E9CCFF]" />
                            </div>
                            <h3 className="font-display text-white text-[16.5px] font-semibold tracking-tight leading-snug mt-5">
                                {p.title}
                            </h3>
                            <p className="text-[#B0A3C4] text-[13.5px] leading-relaxed mt-2 flex-1">
                                {p.desc}
                            </p>
                        </motion.div>
                    );
                })}
            </motion.div>
        </div>
    </section>
);

const PartnerLogos = () => (
    <section className="sxe-section relative bg-[#08031a]" data-testid="about-partners-section">
        <BgLayer variant="orbs" />
        <div className="sxe-container relative">
            <div className="max-w-3xl">
                <div className="overline"><span className="dot" /> Trusted Technology Partnerships</div>
                <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-[1.05] text-white mt-4">
                    Certified alignment with the <span className="text-gradient">world&rsquo;s technology leaders</span>.
                </h2>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-12">
                {PARTNERS.map((p, i) => {
                    const logo = p.slug ? LOGOS[p.slug] : null;
                    return (
                        <motion.div
                            key={p.name}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.35, delay: (i % 10) * 0.03 }}
                            data-testid={`about-partner-${(p.slug || p.name.toLowerCase().replace(/\s+/g, "-"))}`}
                            className="group rounded-xl bg-white h-24 flex items-center justify-center px-4 shadow-[0_8px_20px_-12px_rgba(9,3,20,0.6)] hover:shadow-[0_16px_36px_-16px_rgba(161,0,255,0.35)] hover:-translate-y-0.5 transition-all duration-400"
                        >
                            {logo ? (
                                <img
                                    src={logo}
                                    alt={p.name}
                                    className="max-h-9 max-w-[80%] object-contain group-hover:scale-[1.05] transition-transform duration-500"
                                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                            ) : (
                                <span className="font-display font-semibold text-[15px] text-[#0E1220] tracking-tight text-center">
                                    {p.name}
                                </span>
                            )}
                        </motion.div>
                    );
                })}
            </div>
        </div>
    </section>
);

const Industries = () => (
    <section className="sxe-section relative" data-testid="about-industries-section">
        <BgLayer variant="dots" />
        <div className="sxe-container relative">
            <div className="max-w-3xl">
                <div className="overline"><span className="dot" /> Industries We Serve</div>
                <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-[1.05] text-white mt-4">
                    Impact across <span className="text-gradient">every enterprise vertical</span>.
                </h2>
                <p className="text-[#B0A3C4] text-lg leading-relaxed mt-6">
                    IT, BFSI, Manufacturing, Healthcare, Retail, Telecom, Automotive, Energy, Government, Education, Logistics, and Consulting.
                </p>
            </div>

            <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-40px" }}
                variants={container}
                className="mt-10 flex flex-wrap gap-2.5"
            >
                {INDUSTRIES.map((n) => (
                    <motion.span
                        key={n}
                        variants={item}
                        className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-[13.5px] font-display text-[#E9CCFF] hover:border-[#C266FF]/50 hover:text-white transition-colors"
                        data-testid={`about-industry-${n.toLowerCase()}`}
                    >
                        <Globe2 className="w-3.5 h-3.5 text-[#C266FF]" /> {n}
                    </motion.span>
                ))}
            </motion.div>
        </div>
    </section>
);

const CTA = () => (
    <section className="relative py-20 md:py-28 overflow-hidden" data-testid="about-cta-section">
        <BgLayer variant="mesh" intensity="strong" />
        <div className="sxe-container relative">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55 }}
                className="relative rounded-3xl overflow-hidden border border-[#C266FF]/25 p-10 md:p-16 bg-gradient-to-br from-[#A100FF]/12 to-[#7B00CC]/[0.05]"
            >
                <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#A100FF]/20 blur-3xl" />
                <div className="relative grid lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
                    <div>
                        <div className="overline mb-4"><span className="dot" /> Call to Action</div>
                        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.04] text-white">
                            Ready to <span className="text-gradient">future-proof your workforce</span>?
                        </h2>
                        <p className="text-[#E9CCFF] text-lg leading-relaxed mt-6 max-w-xl">
                            Connect with SpringXE to explore customized enterprise learning solutions.
                        </p>
                    </div>
                    <div className="flex flex-col gap-3 justify-self-start lg:justify-self-end">
                        <a href="#contact" className="btn-primary" data-testid="about-final-cta">
                            <Rocket className="w-4 h-4" /> Talk to Our Experts
                        </a>
                        <Link to="/partners" className="btn-secondary" data-testid="about-final-cta-partners">
                            Explore Partner Programs
                        </Link>
                    </div>
                </div>
            </motion.div>
        </div>
    </section>
);

const About = () => (
    <div data-testid="about-page">
        <Hero />
        <WhoWeAre />
        <MissionVision />
        <WhyChoose />
        <Portfolio />
        <PartnerLogos />
        <Industries />
        <CTA />
    </div>
);

export default About;
